import { AxiosInstance } from "axios";
import { UUID } from "crypto";

export enum UserRole {
  ADMIN = "ADMIN",
  USER = "USER",
}

export type Organisation = {
  id: UUID;
  name: string;
  created_at: string;
  updated_at: string;
};

export type AssignedResource = {
  id: string;
  name: string;
};

export type OrganisationUser = {
  id: UUID;
  email: string;
  role: UserRole;
  active: boolean;
  onboarded?: boolean;
  assigned_assistants: AssignedResource[];
  assigned_knowledge_bases: AssignedResource[];
};

export type OrganisationInvitation = {
  id: UUID;
  email: string;
  role: UserRole;
  expires_at: string;
  accepted: boolean;
  created_at: string;
};

export type CreateOrganisationInput = {
  name: string;
};

export type UpdateOrganisationInput = {
  name: string;
};

export type InviteUserInput = {
  email: string;
  role?: UserRole;
};

export type UpdateUserRoleInput = {
  role: UserRole;
};

// Create organisation
export const createOrganisation = async (
  input: CreateOrganisationInput,
  axiosInstance: AxiosInstance
): Promise<Organisation> => {
  const { data } = await axiosInstance.post<Organisation>(
    "/organisation",
    input
  );

  return data;
};

// Get current user's organisation
export const getCurrentOrganisation = async (
  axiosInstance: AxiosInstance
): Promise<Organisation | null> => {
  const { data } = await axiosInstance.get<Organisation | null>(
    "/organisation"
  );

  return data;
};

// Get organisation by ID
export const getOrganisation = async (
  organisationId: UUID,
  axiosInstance: AxiosInstance
): Promise<Organisation> => {
  const { data } = await axiosInstance.get<Organisation>(
    `/organisation/${organisationId}`
  );

  return data;
};

// Get organisation users
export const getOrganisationUsers = async (
  organisationId: UUID,
  axiosInstance: AxiosInstance
): Promise<OrganisationUser[]> => {
  const { data } = await axiosInstance.get<OrganisationUser[]>(
    `/organisation/${organisationId}/users`
  );

  return data;
};

// Invite user to organisation
export const inviteUser = async (
  organisationId: UUID,
  input: InviteUserInput,
  axiosInstance: AxiosInstance
): Promise<OrganisationInvitation> => {
  const { data } = await axiosInstance.post<OrganisationInvitation>(
    `/organisation/${organisationId}/invite`,
    input
  );

  return data;
};

// Accept organisation invitation
export const acceptOrganisationInvitation = async (
  token: string,
  password: string | null,
  axiosInstance: AxiosInstance
): Promise<Organisation> => {
  const { data } = await axiosInstance.post<Organisation>(
    `/organisation/accept-invitation`,
    { token, password }
  );

  return data;
};

// Update user role
export const updateUserRole = async (
  organisationId: UUID,
  userId: UUID,
  input: UpdateUserRoleInput,
  axiosInstance: AxiosInstance
): Promise<OrganisationUser> => {
  const { data } = await axiosInstance.put<OrganisationUser>(
    `/organisation/${organisationId}/users/${userId}/role`,
    input
  );

  return data;
};

// Update user active status
export const updateUserActiveStatus = async (
  organisationId: UUID,
  userId: UUID,
  active: boolean,
  axiosInstance: AxiosInstance
): Promise<OrganisationUser> => {
  const { data } = await axiosInstance.put<OrganisationUser>(
    `/organisation/${organisationId}/users/${userId}/active?active=${active}`
  );

  return data;
};

// Get pending invitations
export const getPendingInvitations = async (
  organisationId: UUID,
  axiosInstance: AxiosInstance
): Promise<OrganisationInvitation[]> => {
  const { data } = await axiosInstance.get<OrganisationInvitation[]>(
    `/organisation/${organisationId}/invitations`
  );

  return data;
};

// Cancel invitation
export const cancelInvitation = async (
  organisationId: UUID,
  invitationId: UUID,
  axiosInstance: AxiosInstance
): Promise<void> => {
  await axiosInstance.delete(
    `/organisation/${organisationId}/invitations/${invitationId}`
  );
};

// Get current user role
export const getCurrentUserRole = async (
  axiosInstance: AxiosInstance
): Promise<{ role: UserRole | null }> => {
  const { data } = await axiosInstance.get<{ role: UserRole | null }>(
    "/organisation/user/role"
  );

  return data;
};

export const updateOrganisation = async (
  organisationId: UUID,
  input: UpdateOrganisationInput,
  axiosInstance: AxiosInstance
): Promise<Organisation> => {
  const { data } = await axiosInstance.put<Organisation>(
    `/organisation/${organisationId}`,
    input
  );

  return data;
};

// Access management APIs

// Assign assistant to user
export const assignAssistantToUser = async (
  organisationId: UUID,
  userId: UUID,
  assistantId: string,
  axiosInstance: AxiosInstance
): Promise<{ message: string }> => {
  const { data } = await axiosInstance.post<{ message: string }>(
    `/organisation/${organisationId}/users/${userId}/assistants/${assistantId}`
  );

  return data;
};

// Remove assistant from user
export const removeAssistantFromUser = async (
  organisationId: UUID,
  userId: UUID,
  assistantId: string,
  axiosInstance: AxiosInstance
): Promise<{ message: string }> => {
  const { data } = await axiosInstance.delete<{ message: string }>(
    `/organisation/${organisationId}/users/${userId}/assistants/${assistantId}`
  );

  return data;
};

// Assign knowledge base to user
export const assignKnowledgeBaseToUser = async (
  organisationId: UUID,
  userId: UUID,
  knowledgeBaseId: string,
  axiosInstance: AxiosInstance
): Promise<{ message: string }> => {
  const { data } = await axiosInstance.post<{ message: string }>(
    `/organisation/${organisationId}/users/${userId}/knowledge-bases/${knowledgeBaseId}`
  );

  return data;
};

// Remove knowledge base from user
export const removeKnowledgeBaseFromUser = async (
  organisationId: UUID,
  userId: UUID,
  knowledgeBaseId: string,
  axiosInstance: AxiosInstance
): Promise<{ message: string }> => {
  const { data } = await axiosInstance.delete<{ message: string }>(
    `/organisation/${organisationId}/users/${userId}/knowledge-bases/${knowledgeBaseId}`
  );

  return data;
};

// Get available assistants for admin to assign
export const getAvailableAssistants = async (
  organisationId: UUID,
  axiosInstance: AxiosInstance
): Promise<AssignedResource[]> => {
  const { data } = await axiosInstance.get<AssignedResource[]>(
    `/organisation/${organisationId}/available-assistants`
  );

  return data;
};

// Get available knowledge bases for admin to assign
export const getAvailableKnowledgeBases = async (
  organisationId: UUID,
  axiosInstance: AxiosInstance
): Promise<AssignedResource[]> => {
  const { data } = await axiosInstance.get<AssignedResource[]>(
    `/organisation/${organisationId}/available-knowledge-bases`
  );

  return data;
};
